import React, { useEffect } from "react";
import { Navigate, useNavigate, useParams } from "react-router";

import LoadingScreen from "../../../utils/LoadingScreen";
import { usePricing } from "../../../contexts/PricingContext";
import { useNotification } from "../../../contexts/NotificationContext";
import PageHeader from "../../components/pricing/PageHeader";
import ZoneForm, { PRICING_PATH } from "../../forms/ZoneForm";
import PlaceForm from "../../forms/PlaceForm";
import RouteForm from "../../forms/RouteForm";
import ExtraForm from "../../forms/ExtraForm";

const ENTITIES = {
  zones: { eyebrow: "Regiões", add: "Adicionar região", edit: "Editar região" },
  places: { eyebrow: "Locais", add: "Adicionar local", edit: "Editar local" },
  routes: { eyebrow: "Tarifas", add: "Adicionar tarifa", edit: "Editar tarifa" },
  extras: { eyebrow: "Adicionais", add: "Adicionar adicional", edit: "Editar adicional" },
} as const;

type Entity = keyof typeof ENTITIES;

const isEntity = (value: string | undefined): value is Entity =>
  !!value && Object.prototype.hasOwnProperty.call(ENTITIES, value);

/** Página única de criar/editar para as quatro coleções da tabela de preços. */
export default function PricingEntityFormPage() {
  const { entity, id } = useParams();
  const { data, loading } = usePricing();
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const valid = isEntity(entity);
  const record = valid && id ? (data[entity] as Record<string, any>)[id] : undefined;
  const missing = valid && !loading && id !== undefined && !record;

  useEffect(() => {
    if (missing) {
      showNotification("warning", "Registro não encontrado.");
      navigate(`${PRICING_PATH}?tab=${entity}`, { replace: true });
    }
  }, [missing, entity, navigate, showNotification]);

  if (!valid) return <Navigate to={PRICING_PATH} replace />;
  if (loading || missing) return <LoadingScreen />;

  const meta = ENTITIES[entity];

  const form = {
    zones: <ZoneForm id={id} zone={record} />,
    places: <PlaceForm id={id} place={record} />,
    routes: <RouteForm id={id} route={record} />,
    extras: <ExtraForm id={id} extra={record} />,
  }[entity];

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-6 text-white">
      <PageHeader
        eyebrow={meta.eyebrow}
        title={id ? meta.edit : meta.add}
        backTo={`${PRICING_PATH}?tab=${entity}`}
      />
      {form}
    </div>
  );
}
